import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { ShoppingBag, Plus, Minus, Trash2, X } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { formatCurrency } from '../lib/utils';
import { useTranslation } from 'react-i18next';
import { getStorefrontLabels } from './storefront/storefrontLabels';
import { useCurrencyConversion } from '../contexts/CurrencyContext';

const ARABIC_DIGITS = ['٠','١','٢','٣','٤','٥','٦','٧','٨','٩'];
const toLocaleDigits = (num, lang) => {
  if (!lang || !lang.startsWith('ar')) return String(num);
  return String(num).replace(/\d/g, d => ARABIC_DIGITS[parseInt(d)]);
};

// Inline converted price for cart
const CartConvertedPrice = ({ price }) => {
  const { showConversion, convertPrice, formatConverted, storeCurrency } = useCurrencyConversion();
  if (!showConversion) return null;
  const converted = convertPrice(price, storeCurrency);
  if (converted == null) return null;
  return (
    <span className="text-[10px] text-muted-foreground block" data-testid="cart-converted-price">
      {formatConverted(converted)}
    </span>
  );
};

export const CartSidebar = ({ open: openProp, onOpenChange: onOpenChangeProp, currency: currencyProp, onCheckout, labels: labelsProp, subdomainOverride }) => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount, isOpen, setIsOpen, storeCurrency } = useCart();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { subdomain: paramSubdomain } = useParams();
  const labels = labelsProp || getStorefrontLabels(i18n.language);
  const currency = currencyProp || storeCurrency || 'EUR';

  // Use props if provided, otherwise fall back to CartContext state
  const open = openProp !== undefined ? openProp : isOpen;
  const onOpenChange = onOpenChangeProp || setIsOpen;

  const total = getCartTotal();
  const count = getCartCount();

  const handleCheckout = () => {
    if (onCheckout) {
      onCheckout();
    } else {
      const basePath = subdomainOverride ? '' : (paramSubdomain ? `/store/${paramSubdomain}` : '');
      onOpenChange(false);
      navigate(`${basePath}/checkout`);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0" data-testid="cart-sidebar">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-foreground">
              <ShoppingBag className="h-5 w-5" />
              {labels.myCart || 'My cart'} ({count})
            </SheetTitle>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)} data-testid="cart-close-btn">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="cart-empty">
            <ShoppingBag className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="font-semibold text-foreground">{labels.emptyCart || 'Empty cart'}</p>
            <p className="text-sm text-muted-foreground mt-1">{labels.addProductsStart || 'Add products to get started'}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="cart-items">
              {cart.map((item, index) => (
                <div key={index} className="flex gap-3 pb-4 border-b border-border/50 last:border-0" data-testid={`cart-item-${index}`}>
                  {item.image && (
                    <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-muted">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-sm text-foreground line-clamp-2">{item.name}</p>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() => removeFromCart(index)}
                        data-testid={`cart-remove-${index}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {item.selectedOptions?.length > 0 && (
                      <div className="mt-0.5">
                        {Object.entries(
                          item.selectedOptions.reduce((acc, opt) => {
                            const g = opt.group || '';
                            if (!acc[g]) acc[g] = [];
                            acc[g].push(opt);
                            return acc;
                          }, {})
                        ).map(([group, opts]) => (
                          <p key={group} className="text-[11px] text-muted-foreground">
                            {group}: {opts.map(o => o.label).join(', ')}
                          </p>
                        ))}
                      </div>
                    )}
                    {item.notes && (
                      <div className="mt-1 px-2 py-1 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50">
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 italic" data-testid={`cart-item-note-${index}`}>
                          <span className="font-semibold not-italic">Note:</span> {item.notes}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 bg-muted rounded-lg">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(index, item.quantity - 1)}
                          data-testid={`cart-qty-minus-${index}`}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm font-semibold w-5 text-center text-foreground" data-testid={`cart-qty-${index}`}>{toLocaleDigits(item.quantity, i18n?.language)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(index, item.quantity + 1)}
                          data-testid={`cart-qty-plus-${index}`}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <p className="font-semibold text-sm text-foreground text-right">
                        {formatCurrency((item.total_price || item.price || 0) * (item.quantity || 1), currency)}
                        <CartConvertedPrice price={(item.total_price || item.price || 0) * (item.quantity || 1)} />
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t px-6 py-4 space-y-3" data-testid="cart-footer">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">{labels.subtotal || 'Subtotal'} ({count} {count > 1 ? (labels.articles || 'items') : (labels.article || 'item')})</span>
                <div className="text-right">
                  <span className="font-bold text-lg text-foreground" data-testid="cart-total">{formatCurrency(total, currency)}</span>
                  <CartConvertedPrice price={total} />
                </div>
              </div>
              <Button
                className="w-full h-12 font-semibold text-base text-white hover:opacity-90"
                style={{ background: 'var(--t-button-bg, var(--t-accent-bg, hsl(var(--primary))))' }}
                onClick={handleCheckout}
                data-testid="cart-checkout-btn"
              >
                {labels.proceedToCheckout || 'Proceed to checkout'} — {formatCurrency(total, currency)}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
